import { AppLink } from '../components/AppLink';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Icon } from '../components/Icon';
import { Picture } from '../components/Picture';
import { metrics, principal, principles, studioStats, team, workshop } from '../data/about';
import { breadcrumbLd, Seo } from '../lib/seo';
import { absoluteUrl, SITE } from '../lib/site';
import { container, eyebrow, h2Section } from '../lib/ui';

const PATH = '/about';
const TITLE = 'About Our Nagercoil Design Studio';
const DESCRIPTION =
  'Meet Green Valley Interiors, the Nagercoil interior design studio pairing Tamil Nadu stone and teak craft with modern restraint: our story, principles and team.';
const TRAIL: Array<[string, string]> = [['About', PATH]];

// "Monday – Saturday · 9:00 AM – 7:30 PM IST" → ["Monday – Saturday", "9:00 AM – 7:30 PM IST"]
const [hoursDays, hoursTime] = SITE.hours.display.split(' · ');

export default function About() {
  return (
    <>
      <Seo
        title={TITLE}
        description={DESCRIPTION}
        keywords={[
          'interior design studio in Nagercoil',
          'about Green Valley Interiors Nagercoil',
          'interior architects team in Kanyakumari district',
          'bespoke teak joinery workshop in Nagercoil',
          'experienced interior designers in Tamil Nadu',
          'turnkey interior design firm near Trivandrum',
        ]}
        path={PATH}
        jsonLd={[
          breadcrumbLd(TRAIL),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            '@id': `${absoluteUrl(PATH)}#webpage`,
            url: absoluteUrl(PATH),
            name: `${TITLE} | ${SITE.name}`,
            description: DESCRIPTION,
            inLanguage: 'en-IN',
            isPartOf: { '@id': `${SITE.origin}/#website` },
            about: { '@id': `${SITE.origin}/#business` },
          },
        ]}
      />

      {/* HERO / MANIFESTO */}
      <section className="relative w-full overflow-hidden bg-surface-ivory pt-space-lg md:pt-space-xl pb-24 md:pb-36">
        <div className="absolute -top-32 right-1/4 w-[500px] h-[500px] rounded-full bg-emerald-vivid/5 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -left-20 w-[420px] h-[420px] rounded-full bg-secondary-fixed/30 blur-3xl pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <Breadcrumbs trail={TRAIL} className="mb-space-md" />
          <div className="flex flex-col lg:flex-row lg:items-baseline justify-between gap-space-sm lg:gap-6 pb-space-lg md:pb-12">
            <div className="flex items-center gap-space-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-vivid" />
              <span className="font-label-md text-label-md uppercase tracking-[0.25em] text-secondary">Atelier Manifesto · Est. 2012</span>
            </div>
            <div className="font-label-sm text-label-sm uppercase tracking-[0.2em] text-on-surface-variant flex items-center gap-3">
              <span>Nagercoil</span>
              <span className="w-1 h-1 rounded-full bg-outline-variant" />
              <span>Kanyakumari</span>
              <span className="w-1 h-1 rounded-full bg-outline-variant" />
              <span>Trivandrum</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-end mb-space-xl md:mb-16">
            <div className="lg:col-span-8">
              <h1 className="flex flex-col gap-space-sm animate-rise">
                <span className="font-label-md text-label-md uppercase tracking-[0.2em] text-primary">
                  About Green Valley Interiors — Nagercoil Interior Design Studio
                </span>
                <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary !leading-[1.08] tracking-tight">
                  The Philosophy of <span className="italic font-normal text-emerald-vivid">Poise</span>, <br className="hidden sm:inline" />
                  Provenance <span className="text-secondary font-normal">&amp;</span> Space.
                </span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pl-6">
              <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed animate-rise [animation-delay:80ms]">
                We orchestrate living volumes that balance the contemplative silence of ancient stone with the fluid
                precision of cosmopolitan modernism.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter items-stretch">
            <div className="md:col-span-7 relative group overflow-hidden rounded-lg shadow-xl min-h-[380px] md:min-h-[460px] lg:min-h-[560px] bg-surface-container">
              <Picture
                name="about-hero-living-room"
                alt="Sunlit double-height living room with limestone walls, dark wood millwork, a travertine plinth and linen upholstery"
                sizes="(min-width: 768px) 58vw, 100vw"
                priority
                className="absolute inset-0 block w-full h-full"
                imgClassName="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-botanical-dark/75 via-botanical-dark/20 to-transparent flex flex-col justify-end p-space-md md:p-8 text-on-primary">
                <span className="font-label-sm text-label-sm text-gold-light uppercase tracking-widest mb-1">
                  Residence Horizon · Kanyakumari Coast
                </span>
                <p className="font-headline-sm text-headline-sm font-light text-surface-bright">
                  “Space is not void; it is the physical medium through which tranquility is conducted.”
                </p>
              </div>
            </div>
            <div className="md:col-span-5 flex flex-col gap-gutter justify-between">
              <div className="relative overflow-hidden rounded-lg shadow-md h-[270px] bg-surface-container">
                <Picture
                  name="about-teak-fluted-detail"
                  alt="Close-up of fluted dark teak wall panelling meeting brushed brass hardware and a micro-cement edge"
                  sizes="(min-width: 768px) 40vw, 100vw"
                  className="block w-full h-full"
                  imgClassName="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-105"
                />
                <div className="absolute top-4 right-4 bg-surface-ivory/90 backdrop-blur-md px-3 py-1 rounded text-primary font-label-sm text-label-sm uppercase tracking-wider">
                  Material Honesty
                </div>
              </div>
              <div className="bg-surface-cream rounded-lg p-space-md md:p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <Icon name="architecture" size={30} className="text-secondary mb-4" />
                  <p className="font-headline-sm text-headline-sm text-primary mb-3">Spatial Sanctuary</p>
                  <p className="font-body-md text-body-md text-on-surface-variant">
                    Every threshold, shadowline, and aperture is calibrated to purge visual chaos, crafting environments where
                    human emotion breathes unconstrained.
                  </p>
                </div>
                <a href="#principles" className="pt-6 flex items-center justify-between text-secondary group">
                  <span className="font-label-md text-label-md uppercase tracking-wider text-primary font-semibold">Our Studio Axioms</span>
                  <Icon name="east" size={20} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STUDIO STORY */}
      <section className="w-full bg-botanical-dark text-on-primary py-space-xl md:py-32 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-emerald-vivid/10 to-transparent pointer-events-none" />
        <div className={`${container} relative z-10`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter gap-y-space-xl items-center">
            <div className="lg:col-span-6 relative reveal">
              <div className="relative rounded-lg overflow-hidden shadow-2xl bg-primary-container">
                <Picture
                  name="about-atelier-workshop"
                  alt="Artisans carving timber joinery in the Green Valley workshop under sunlight from tall clerestory windows"
                  sizes="(min-width: 1024px) 45vw, 100vw"
                  className="block w-full"
                  imgClassName="w-full h-[360px] md:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-botanical-dark/80 via-transparent to-transparent" />
              </div>
              <div className="hidden sm:block absolute -bottom-8 -right-2 lg:-right-6 bg-primary-container p-6 rounded-lg shadow-2xl max-w-xs text-on-primary">
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light mb-1 block">
                  Headquarters &amp; Joinery
                </span>
                <p className="font-body-sm text-body-sm text-surface-bright leading-relaxed">
                  {SITE.address.line1}, {SITE.address.line2}, {SITE.address.locality}, {SITE.address.region}.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Icon name="pin_drop" size={16} className="text-emerald-vivid" />
                  <span className="font-label-sm text-label-sm tracking-wider uppercase text-gold-light">Studio &amp; Foundry</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 flex flex-col justify-center lg:pl-10 reveal">
              <div className="flex items-center gap-2 mb-4">
                <span className="h-px w-8 bg-secondary" />
                <span className="font-label-md text-label-md uppercase tracking-[0.2em] text-secondary-fixed-dim">The Studio Story</span>
              </div>
              <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-ivory mb-6 !leading-tight">
                Rooted in Dravidian Artisanship, <br className="hidden md:inline" />
                Refined by <span className="italic font-normal text-gold-light">Continental Clarity</span>.
              </h2>
              <p className="font-body-lg text-body-lg text-on-primary-container mb-6 leading-relaxed">
                Green Valley was inaugurated in 2012 within the coastal hinterlands of Nagercoil, Tamil Nadu. Surrounded by
                centuries of monolithic granite stonemasonry and indigenous teak woodcraft, our founders recognized a rare
                opportunity: to synthesize native architectural vernacular with the rational restraint of European
                modernism.
              </p>
              <p className="font-body-md text-body-md text-surface-container-highest/80 mb-8 leading-relaxed">
                From our primary design chambers at Praise Complex, our atelier commands an integrated network of
                generational joiners, stone carvers, and metallurgical artisans. We eliminate the friction between
                conceptual sketches and final physical execution, ensuring an uninterrupted lineage of quality.
              </p>
              <dl className="grid grid-cols-2 gap-4">
                {studioStats.map((s) => (
                  <div key={s.label} className="bg-primary/60 p-4 rounded flex flex-col-reverse justify-end">
                    <dt className="font-label-sm text-label-sm uppercase tracking-wider text-surface-dim">{s.label}</dt>
                    <dd className="font-headline-sm text-headline-sm text-gold-light">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section id="principles" className="w-full bg-surface-cream py-space-xl md:py-36 scroll-mt-20">
        <div className={container}>
          <div className="reveal flex flex-col md:flex-row md:items-end justify-between mb-space-xl md:mb-16 gap-6">
            <div>
              <span className={`${eyebrow} mb-3 block`}>Non-Negotiable Convictions</span>
              <h2 className={`${h2Section} tracking-tight`}>The Four Principles of Form</h2>
            </div>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-md">
              We reject decorative excess and fleeting styling fads. Every spatial decision is rigorously vetted against our
              timeless architectural matrix.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter">
            {principles.map((p) => (
              <article
                key={p.number}
                className="reveal bg-surface-ivory rounded-lg p-space-md md:p-8 flex flex-col justify-between shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
              >
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <span
                      aria-hidden="true"
                      className="font-headline-lg text-headline-lg text-secondary/30 font-light group-hover:text-secondary transition-colors"
                    >
                      {p.number}
                    </span>
                    <Icon name={p.icon} size={26} className="text-primary" />
                  </div>
                  <h3 className="font-headline-sm text-headline-sm text-primary mb-3">{p.title}</h3>
                  <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">{p.text}</p>
                </div>
                <div className="mt-8 pt-4">
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-secondary block">{p.tag}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="w-full bg-primary text-on-primary py-space-xl md:py-20 relative" aria-label="The atelier in numbers">
        <div className={container}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 text-center sm:text-left">
            {metrics.map((m) => (
              <div key={m.label} className="reveal flex flex-col gap-2">
                <dt className="order-2 font-headline-sm text-headline-sm text-surface-ivory">{m.label}</dt>
                <dd className="order-1 font-headline-xl text-headline-xl-mobile md:text-headline-xl text-gold-light tracking-tight">
                  {m.value}
                  {m.suffix && <span className="text-emerald-vivid">{m.suffix}</span>}
                </dd>
                <dd className="order-3 font-body-sm text-body-sm text-on-primary-container">{m.text}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* TEAM */}
      <section className="w-full bg-surface-ivory py-space-xl md:py-36">
        <div className={container}>
          <div className="reveal max-w-2xl mb-space-xl md:mb-16">
            <span className={`${eyebrow} mb-3 block`}>Guild Leadership</span>
            <h2 className={`${h2Section} tracking-tight`}>The Minds &amp; Hands Sculpting Your Realm</h2>
            <p className="font-body-md text-body-md text-on-surface-variant mt-4">
              Our leadership combines decades of rigorous architectural methodology with hands-on timber joinery and
              structural precision.
            </p>
          </div>

          <article className="reveal bg-surface-cream rounded-xl p-space-md md:p-8 lg:p-12 mb-space-xl md:mb-16 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter gap-y-space-lg items-center">
              <div className="lg:col-span-5">
                <div className="relative overflow-hidden rounded-lg shadow-md aspect-[4/5] bg-surface-container">
                  <Picture
                    name={principal.image}
                    alt={principal.imageAlt}
                    sizes="(min-width: 1024px) 36vw, 100vw"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-4 left-4 right-4 sm:right-auto bg-botanical-dark/90 text-gold-light px-3 py-1.5 rounded font-label-sm text-label-sm uppercase tracking-wider backdrop-blur-sm">
                    {principal.credentials}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7 flex flex-col justify-center lg:pl-6">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                  <span className="font-label-md text-label-md uppercase tracking-widest text-secondary">{principal.role}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-vivid" />
                  <span className="font-label-sm text-label-sm text-on-surface-variant">{principal.subRole}</span>
                </div>
                <h3 className="font-headline-md text-headline-md text-primary mb-4">{principal.name}</h3>
                <blockquote className="font-headline-sm text-[20px] leading-8 font-normal italic text-primary/80 mb-6">
                  “{principal.quote}”
                </blockquote>
                <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-6">{principal.bio}</p>
                <ul className="flex flex-wrap gap-space-sm pt-2">
                  {principal.specialisms.map((s) => (
                    <li key={s} className="px-3 py-1 bg-surface-ivory rounded font-label-sm text-label-sm uppercase tracking-wider text-primary">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </article>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {team.map((m) => (
              <article key={m.name} className="reveal bg-surface-cream rounded-lg overflow-hidden shadow-sm flex flex-col">
                <div className="aspect-[4/3] overflow-hidden bg-surface-container">
                  <Picture
                    name={m.image}
                    alt={m.imageAlt}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow justify-between">
                  <div>
                    <span className="font-label-sm text-label-sm uppercase tracking-wider text-secondary">{m.role}</span>
                    <h3 className="font-headline-sm text-headline-sm text-primary mt-1 mb-2">{m.name}</h3>
                    <p className="font-body-sm text-body-sm text-on-surface-variant">{m.text}</p>
                  </div>
                  <span className="font-label-sm text-label-sm text-emerald-vivid mt-4 font-semibold">{m.experience}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIAL LAB */}
      <section className="w-full bg-surface-container py-space-xl md:py-36">
        <div className={container}>
          <div className="reveal grid grid-cols-1 lg:grid-cols-12 gap-gutter gap-y-space-sm items-end mb-space-xl md:mb-16">
            <div className="lg:col-span-7">
              <span className={`${eyebrow} mb-3 block`}>Provenance &amp; Material Lab</span>
              <h2 className={`${h2Section} tracking-tight`}>Where Raw Matter Becomes Stillness</h2>
            </div>
            <div className="lg:col-span-5">
              <p className="font-body-md text-body-md text-on-surface-variant">
                We spend months vetting our raw minerals and biological substrates. No material enters a Green Valley
                commission without thorough sensory and climate testing.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {workshop.map((w) => (
              <article key={w.title} className="reveal bg-surface-ivory rounded-lg overflow-hidden shadow-md flex flex-col">
                <div className="h-64 overflow-hidden relative bg-surface-container-high">
                  <Picture
                    name={w.image}
                    alt={w.imageAlt}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    className="block w-full h-full"
                    imgClassName="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute bottom-3 left-3 bg-botanical-dark/80 backdrop-blur-sm text-gold-light text-label-sm font-label-sm px-2.5 py-1 rounded">
                    {w.badge}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-headline-sm text-headline-sm text-primary mb-2">{w.title}</h3>
                  <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">{w.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STUDIO VISIT CTA */}
      <section className="w-full bg-botanical-dark text-on-primary py-space-xl md:py-32 relative overflow-hidden">
        <div className={`${container} relative z-10`}>
          <div className="reveal bg-primary-container/60 backdrop-blur-xl rounded-2xl p-space-md sm:p-8 md:p-16 relative overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-emerald-vivid animate-pulse" />
                  <span className="font-label-sm text-label-sm uppercase tracking-[0.25em] text-gold-light">Exclusive Appointment</span>
                </div>
                <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-ivory mb-6 !leading-tight">
                  Experience the Provenance. <br className="hidden md:inline" />
                  Schedule a Studio &amp; Material Walk-Through.
                </h2>
                <p className="font-body-lg text-body-lg text-on-primary-container max-w-2xl leading-relaxed mb-8">
                  We welcome patrons contemplating substantial architectural commissions to tour our private drafting rooms,
                  material archives, and joinery workshops at Praise Complex, Nagercoil.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                  <AppLink
                    href="/contact"
                    className="inline-flex items-center justify-center bg-secondary hover:bg-gold-dark text-on-primary font-label-lg text-label-lg uppercase tracking-wider px-8 py-4 rounded transition-all duration-300 shadow-md text-center"
                  >
                    <span>Request Private Consultation</span>
                    <Icon name="calendar_month" size={18} className="ml-2" />
                  </AppLink>
                  <a
                    href={`tel:${SITE.phoneE164}`}
                    className="inline-flex items-center justify-center bg-transparent hover:bg-surface-ivory/10 text-gold-light font-label-lg text-label-lg uppercase tracking-wider px-4 sm:px-8 py-4 rounded transition-colors text-center"
                  >
                    <Icon name="call" size={18} className="mr-2" />
                    <span>Direct Concierge: {SITE.phoneDisplay}</span>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-4 mt-8 lg:mt-0 flex flex-col justify-center bg-primary/80 p-space-md sm:p-8 rounded-xl shadow-inner">
                <span className="font-label-sm text-label-sm uppercase tracking-widest text-gold-light mb-3">Atelier Hours &amp; Access</span>
                <dl className="space-y-4 font-body-sm text-body-sm text-surface-bright">
                  <div className="flex justify-between gap-4 pb-2">
                    <dt>{hoursDays}:</dt>
                    <dd className="text-gold-light font-medium text-right">{hoursTime}</dd>
                  </div>
                  <div className="flex justify-between gap-4 pb-2">
                    <dt>Sunday:</dt>
                    <dd className="text-surface-dim text-right">By Prior Arrangement</dd>
                  </div>
                </dl>
                <div className="mt-6 pt-4 flex items-center gap-3 text-gold-light">
                  <Icon name="location_city" size={20} />
                  <span className="font-label-sm text-label-sm uppercase tracking-wider">Tamil Nadu · Praise Complex</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
